import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Skeleton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSettingsContext } from '../Settings/hooks/useSettingsContext';
import { useFeaturesEnabled } from '../ErpFeatures/hooks/useErpFeatures';
import { ErpFeatureKeys } from '../ErpFeatures/models/erpFeature';
import API from '@/services/api';

export type FAQ = {
  id: number;
  pregunta: string;
  resposta: string;
};

const FAQItem = ({ pregunta, resposta }: Omit<FAQ, 'id'>) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        mb: expanded ? 2 : 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'background.paper',
        '&:before': { display: 'none' },
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover',
        },
        transition: 'margin-bottom 0.2s ease',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          minHeight: 56,
          cursor: 'pointer',
          touchAction: 'manipulation',
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: 'text.secondary',
          },
        }}
      >
        <Typography
          component="div"
          sx={{
            pr: 1,
            fontWeight: 500,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.4,
          }}
        >
          {pregunta}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        onClick={() => setExpanded(false)}
        sx={{
          px: { xs: 1.5, sm: 2 },
          pb: 2,
          pt: 0,
          cursor: 'pointer',
        }}
      >
        <Typography
          component="div"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            lineHeight: 1.6,
          }}
        >
          {resposta}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export const FAQPage = () => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  const faqSettingEnabled = Settings.features?.showFaqs ?? false;
  const faqFeatureEnabled = useFeaturesEnabled([
    ErpFeatureKeys.FAQS_API,
    ErpFeatureKeys.FAQS_ERP,
  ]);
  const enabled = faqSettingEnabled && faqFeatureEnabled;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setFaqs([]);
      return;
    }

    setLoading(true);
    let cancelled = false;

    API.get<FAQ[]>({ endpoint: '/faqs/', versions: [1] })
      .then((res) => {
        if (!cancelled) {
          setFaqs(res.promise.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFaqs([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled) {
    return <Navigate to={t('common:url.error')} replace />;
  }

  if (loading) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 0, sm: 2 }, py: 4 }}>
        <Typography variant="h4" sx={{ px: { xs: 1.5, sm: 0 }, mb: 3 }}>
          {t('common:faqs.title')}
        </Typography>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={56}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  if (!faqs.length) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto'}}>
        <Typography>{t('common:faqs.empty')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto'}}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('common:faqs.title')}
      </Typography>
      {faqs.map((faq) => (
        <FAQItem
          key={faq.id}
          pregunta={faq.pregunta}
          resposta={faq.resposta}
        />
      ))}
    </Box>
  );
};

export default FAQPage;
